<template>
  <ListPageShell>
    <template #header>
      <div class="rounded-xl border border-slate-200 bg-white px-5 py-6 shadow-sm sm:px-6">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <Cog6ToothIcon class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-xl font-semibold text-slate-900">日报治理</h1>
            <p class="mt-0.5 text-sm text-slate-500">维护报送规则、创建人工豁免，并识别组织数据对日报流转的影响。</p>
          </div>
        </div>
      </div>
    </template>

    <div class="space-y-4">
      <nav class="flex flex-wrap gap-1.5 rounded-xl bg-slate-100 p-1" aria-label="日报治理分类">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          class="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200"
          :class="activeTab === tab.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
          @click="activeTab = tab.value"
        >
          {{ tab.label }}
        </button>
      </nav>

      <section v-if="activeTab === 'rules'">
        <div class="list-page-table-panel">
        <PageFilterBar
          shell-class="filter-bar-shell is-embedded"
          body-class="filter-bar-body is-panel"
          :show-refresh="true"
          :refresh-loading="rulesLoading"
          @refresh="loadRules"
        >
          <el-select v-model="ruleFilter.scope" placeholder="请选择范围" class="form-select form-control-sm filter-bar-field">
            <el-option label="全部范围" value="" />
            <el-option label="租户默认" value="TENANT" />
            <el-option label="部门" value="DEPARTMENT" />
            <el-option label="员工" value="EMPLOYEE" />
          </el-select>
          <template #searchActions>
            <button type="button" class="btn-secondary" :disabled="rulesLoading" @click="resetRuleFilter">重置</button>
            <button type="button" class="btn-primary" @click="openRuleDrawer">
              <PlusIcon class="h-4 w-4" />
              新增规则
            </button>
          </template>
        </PageFilterBar>

        <div class="data-table-shell">
          <div v-if="rulesLoading" class="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <div class="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
            <span class="text-sm text-slate-500">正在加载报送规则...</span>
          </div>
          <div v-else-if="rulesLoadError" class="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
              <ExclamationTriangleIcon class="h-6 w-6 text-rose-500" />
            </div>
            <p class="text-sm text-slate-600">报送规则加载失败，请稍后重试。</p>
            <button type="button" class="btn-secondary mt-1" @click="loadRules">重试</button>
          </div>
          <div v-else-if="filteredRules.length === 0" class="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <InboxIcon class="h-6 w-6 text-slate-400" />
            </div>
            <span class="text-sm text-slate-500">暂无报送规则</span>
          </div>
          <div v-else class="data-table-scroll">
            <table class="data-table">
              <thead class="data-table-head">
                <tr>
                  <th class="data-table-th text-left">范围</th>
                  <th class="data-table-th text-left">目标</th>
                  <th class="data-table-th text-left">版本</th>
                  <th class="data-table-th text-left">生效区间</th>
                  <th class="data-table-th text-left">日报功能</th>
                  <th class="data-table-th text-left">计划截止</th>
                  <th class="data-table-th text-left">计划提醒</th>
                  <th class="data-table-th text-left">总结截止</th>
                  <th class="data-table-th text-left">总结提醒</th>
                  <th class="data-table-th text-left">状态</th>
                </tr>
              </thead>
              <tbody class="data-table-body">
                <tr v-for="rule in filteredRules" :key="rule.id || `${rule.scope}-${rule.scopeTargetId}-${rule.versionNo}`" class="transition-colors hover:bg-slate-50/80">
                  <td class="data-table-td font-medium text-slate-800">{{ scopeLabel(rule.scope) }}</td>
                  <td class="data-table-td text-slate-700">{{ scopeTargetLabel(rule) }}</td>
                  <td class="data-table-td text-slate-700">v{{ rule.versionNo || '--' }}</td>
                  <td class="data-table-td text-slate-700">{{ dateRangeLabel(rule) }}</td>
                  <td class="data-table-td">
                    <span class="status-badge" :class="rule.dailyReportEnabled === false ? 'bg-slate-100 text-slate-600' : 'bg-emerald-50 text-emerald-700'">
                      {{ rule.dailyReportEnabled === false ? '关闭' : '开启' }}
                    </span>
                  </td>
                  <td class="data-table-td text-slate-700">{{ timeLabel(rule.planDeadline) }}</td>
                  <td class="data-table-td text-slate-700">{{ reminderOffsetLabel(rule.teamPlanReminderEnabled, rule.teamPlanReminderOffsetMinutes) }}</td>
                  <td class="data-table-td text-slate-700">{{ timeLabel(rule.summaryDeadline) }}</td>
                  <td class="data-table-td text-slate-700">{{ reminderOffsetLabel(rule.teamSummaryReminderEnabled, rule.teamSummaryReminderOffsetMinutes) }}</td>
                  <td class="data-table-td">
                    <span class="status-badge" :class="rule.active === false ? 'bg-slate-100 text-slate-600' : 'bg-emerald-50 text-emerald-700'">
                      {{ rule.active === false ? '停用' : '启用' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </section>

      <section v-else-if="activeTab === 'forcedRecipients'" class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
        <div class="rounded-xl border border-blue-200 bg-blue-50 px-5 py-5 shadow-sm">
          <h2 class="text-base font-semibold text-blue-900">强制接收人</h2>
          <p class="mt-3 text-sm leading-6 text-blue-700">
            为租户、部门或员工配置强制接收人/抄送人。日报生成时由后端按规则写入接收人快照。
          </p>
        </div>

        <form class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" @submit.prevent="submitForcedRecipient">
          <h3 class="text-base font-semibold text-slate-900">新增强制接收人</h3>
          <div class="mt-4 space-y-4">
            <label class="block">
              <span class="form-label">范围 <span class="text-red-500">*</span></span>
              <el-select v-model="forcedRecipientForm.scope" class="form-select mt-1 w-full">
                <el-option label="租户默认" value="TENANT" />
                <el-option label="部门" value="DEPARTMENT" />
                <el-option label="员工" value="EMPLOYEE" />
              </el-select>
            </label>
            <div v-if="forcedRecipientForm.scope !== 'TENANT'" class="block">
              <span class="form-label">{{ forcedRecipientForm.scope === 'DEPARTMENT' ? '部门' : '员工' }} <span class="text-red-500">*</span></span>
              <DeptSelector
                v-if="forcedRecipientForm.scope === 'DEPARTMENT'"
                v-model="selectedForcedScopeDept"
                class="mt-1"
                :multiple="false"
                placeholder="请选择作用部门"
                @update:model-value="handleForcedScopeDeptChange"
              />
              <UserSelector
                v-else
                v-model="selectedForcedScopeEmployee"
                class="mt-1"
                :multiple="false"
                placeholder="请选择作用员工"
                @update:model-value="handleForcedScopeEmployeeChange"
                @confirm="handleForcedScopeEmployeeConfirm"
              />
            </div>
            <label class="block">
              <span class="form-label">接收人 <span class="text-red-500">*</span></span>
              <UserSelector
                v-model="selectedForcedRecipientUser"
                class="mt-1"
                :multiple="false"
                placeholder="请选择接收人"
                @update:model-value="handleForcedRecipientUserChange"
                @confirm="handleForcedRecipientUserConfirm"
              />
            </label>
            <label class="block">
              <span class="form-label">角色 <span class="text-red-500">*</span></span>
              <el-select v-model="forcedRecipientForm.role" class="form-select mt-1 w-full">
                <el-option label="强制接收人" value="FORCED_RECIPIENT" />
                <el-option label="强制抄送人" value="FORCED_CC" />
              </el-select>
            </label>
            <label class="form-switch">
              <input v-model="forcedRecipientForm.active" class="form-switch-input" type="checkbox">
              <span class="form-switch-track"></span>
              <span class="form-switch-label">启用</span>
            </label>
          </div>
          <div class="mt-4 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="forcedRecipientSubmitting">
              {{ forcedRecipientSubmitting ? '保存中' : '保存强制接收人' }}
            </button>
          </div>
        </form>
      </section>

      <section v-else-if="activeTab === 'exemptions'" class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
        <div class="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 px-5 py-5 shadow-sm">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
              <ShieldExclamationIcon class="h-5 w-5 text-amber-600" />
            </div>
            <h2 class="text-base font-semibold text-amber-900">人工豁免</h2>
          </div>
          <p class="mt-3 text-sm leading-6 text-amber-700">
            当前接口仅支持新增人工豁免。豁免列表与撤销待后端能力提供后启用；若日报已生成，由后端负责刷新资格事实。
          </p>
        </div>

        <form class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" @submit.prevent="submitExemption">
          <h3 class="text-base font-semibold text-slate-900">新增豁免</h3>
          <div class="mt-4 space-y-4">
            <label class="block">
              <span class="form-label">员工 <span class="text-red-500">*</span></span>
              <UserSelector
                v-model="selectedExemptionEmployee"
                class="mt-1"
                :multiple="false"
                placeholder="请选择豁免员工"
                @update:model-value="handleExemptionEmployeeChange"
                @confirm="handleExemptionEmployeeConfirm"
              />
              <p v-if="exemptionForm.employeeUserId" class="mt-1 text-xs text-slate-500">员工 ID：{{ exemptionForm.employeeUserId }}</p>
            </label>
            <label class="block">
              <span class="form-label">豁免日期 <span class="text-red-500">*</span></span>
              <input v-model="exemptionForm.exemptionDate" class="form-input form-date mt-1 w-full" type="date">
            </label>
            <label class="block">
              <span class="form-label">原因 <span class="text-red-500">*</span></span>
              <textarea v-model.trim="exemptionForm.reason" class="form-textarea mt-1 w-full" rows="4" placeholder="请输入豁免原因"></textarea>
            </label>
          </div>
          <div class="mt-4 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="exemptionSubmitting">
              {{ exemptionSubmitting ? '提交中' : '创建豁免' }}
            </button>
          </div>
        </form>
      </section>

      <section v-else class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
            <CheckBadgeIcon class="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 class="text-base font-semibold text-slate-900">组织数据质量</h2>
          </div>
        </div>
        <div class="mt-4 max-w-3xl">
          <p class="mt-2 text-sm leading-6 text-slate-600">
            未配置负责人的部门属于非阻断异常：员工仍可提交日报，但不会产生主管接收或评价任务；负责人补齐后不回填既有日报事实。
          </p>
          <p class="mt-2 text-sm leading-6 text-slate-600">
            负责人缺失列表接口未就绪，因此不展示影响数列表。请前往部门管理维护负责人信息。
          </p>
        </div>
        <div class="mt-5 flex flex-wrap items-end gap-3">
          <label class="block w-full max-w-xs">
            <span class="form-label">部门 ID（可选）</span>
            <input v-model.trim="qualityForm.deptId" class="form-input form-control-sm mt-1 w-full" inputmode="numeric" placeholder="带入部门管理筛选">
          </label>
          <button type="button" class="btn-primary" @click="goDepartmentManagement">
            <ArrowTopRightOnSquareIcon class="h-4 w-4" />
            前往部门管理
          </button>
        </div>
      </section>
    </div>

    <Teleport to="body">
      <div v-if="ruleDrawerVisible" class="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="新增报送规则">
        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="closeRuleDrawer"></div>
        <aside class="absolute inset-y-0 right-0 flex h-full w-full flex-col bg-white shadow-2xl md:w-[540px]">
          <header class="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4">
            <div class="flex items-center gap-3">
              <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <ClipboardDocumentCheckIcon class="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 class="text-lg font-semibold text-slate-900">新增报送规则</h2>
                <p class="mt-0.5 text-sm text-slate-500">保存后由后端完成重叠校验和规则生效判断。</p>
              </div>
            </div>
            <button type="button" class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title="关闭" aria-label="关闭" @click="closeRuleDrawer">
              <XMarkIcon class="h-5 w-5" />
            </button>
          </header>

          <form class="min-h-0 flex-1 overflow-y-auto bg-slate-50 px-5 py-5" @submit.prevent="submitRule">
            <div class="space-y-4">
              <label class="block">
                <span class="form-label">范围 <span class="text-red-500">*</span></span>
                <el-select v-model="ruleForm.scope" class="form-select mt-1 w-full">
                  <el-option label="租户默认" value="TENANT" />
                  <el-option label="部门" value="DEPARTMENT" />
                  <el-option label="员工" value="EMPLOYEE" />
                </el-select>
              </label>

              <div v-if="ruleForm.scope !== 'TENANT'" class="block">
                <span class="form-label">{{ ruleForm.scope === 'DEPARTMENT' ? '部门' : '员工' }} <span class="text-red-500">*</span></span>
                <DeptSelector
                  v-if="ruleForm.scope === 'DEPARTMENT'"
                  v-model="selectedRuleDept"
                  class="mt-1"
                  :multiple="false"
                  placeholder="请选择适用部门"
                  @update:model-value="handleRuleDeptChange"
                />
                <UserSelector
                  v-else
                  v-model="selectedRuleEmployee"
                  class="mt-1"
                  :multiple="false"
                  placeholder="请选择适用员工"
                  @update:model-value="handleRuleEmployeeChange"
                  @confirm="handleRuleEmployeeConfirm"
                />
                <p v-if="ruleForm.scopeTargetId" class="mt-1 text-xs text-slate-500">
                  {{ ruleForm.scope === 'DEPARTMENT' ? '部门 ID' : '员工 ID' }}：{{ ruleForm.scopeTargetId }}
                </p>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <label class="block">
                  <span class="form-label">版本号 <span class="text-red-500">*</span></span>
                  <input v-model.number="ruleForm.versionNo" class="form-input mt-1 w-full" type="number" min="1">
                </label>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <label class="block">
                  <span class="form-label">生效日期 <span class="text-red-500">*</span></span>
                  <input v-model="ruleForm.effectiveFrom" class="form-input form-date mt-1 w-full" type="date">
                </label>
                <label class="block">
                  <span class="form-label">失效日期</span>
                  <input v-model="ruleForm.effectiveTo" class="form-input form-date mt-1 w-full" type="date">
                </label>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <label class="block">
                  <span class="form-label">计划截止</span>
                  <input v-model="ruleForm.planDeadline" class="form-input mt-1 w-full" type="time">
                </label>
                <label class="block">
                  <span class="form-label">总结截止</span>
                  <input v-model="ruleForm.summaryDeadline" class="form-input mt-1 w-full" type="time">
                </label>
              </div>

              <div class="rounded-lg border border-slate-200 bg-white px-3 py-3">
                <label class="form-switch">
                  <input v-model="ruleForm.dailyReportEnabled" class="form-switch-input" type="checkbox">
                  <span class="form-switch-track"></span>
                  <span class="form-switch-label">开启日报要求</span>
                </label>
                <p class="mt-1 text-xs leading-5 text-slate-500">关闭后不计入团队应交和团队提醒，员工仍可主动提交日报。</p>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <div class="rounded-lg border border-slate-200 bg-white px-3 py-3">
                  <label class="form-switch">
                    <input v-model="ruleForm.teamPlanReminderEnabled" class="form-switch-input" type="checkbox">
                    <span class="form-switch-track"></span>
                    <span class="form-switch-label">团队计划提醒</span>
                  </label>
                  <label class="mt-3 block">
                    <span class="form-label">相对计划截止（分钟）</span>
                    <input v-model.number="ruleForm.teamPlanReminderOffsetMinutes" class="form-input mt-1 w-full" type="number" :disabled="!ruleForm.teamPlanReminderEnabled">
                  </label>
                  <p class="mt-1 text-xs text-slate-500">{{ reminderOffsetLabel(ruleForm.teamPlanReminderEnabled, ruleForm.teamPlanReminderOffsetMinutes) }}</p>
                </div>
                <div class="rounded-lg border border-slate-200 bg-white px-3 py-3">
                  <label class="form-switch">
                    <input v-model="ruleForm.teamSummaryReminderEnabled" class="form-switch-input" type="checkbox">
                    <span class="form-switch-track"></span>
                    <span class="form-switch-label">团队总结提醒</span>
                  </label>
                  <label class="mt-3 block">
                    <span class="form-label">相对总结截止（分钟）</span>
                    <input v-model.number="ruleForm.teamSummaryReminderOffsetMinutes" class="form-input mt-1 w-full" type="number" :disabled="!ruleForm.teamSummaryReminderEnabled">
                  </label>
                  <p class="mt-1 text-xs text-slate-500">{{ reminderOffsetLabel(ruleForm.teamSummaryReminderEnabled, ruleForm.teamSummaryReminderOffsetMinutes) }}</p>
                </div>
              </div>

              <p class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-6 text-amber-800">
                重叠风险由后端最终判断；规则变更不会重写已经冻结到日报中的规则快照。
              </p>
            </div>

            <div class="mt-6 flex justify-end gap-3 border-t border-slate-200 pt-5">
              <button type="button" class="btn-secondary" @click="closeRuleDrawer">取消</button>
              <button type="submit" class="btn-primary" :disabled="ruleSubmitting">
                {{ ruleSubmitting ? '保存中' : '保存规则' }}
              </button>
            </div>
          </form>
        </aside>
      </div>
    </Teleport>
  </ListPageShell>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  ArrowTopRightOnSquareIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  InboxIcon,
  PlusIcon,
  ShieldExclamationIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import UserSelector from '@/components/UserSelector/index.vue'
import DeptSelector from '@/views/system/components/DeptSelector/index.vue'
import {
  getWorkReportRules,
  saveForcedReportRecipients,
  saveWorkReportManualExemption,
  saveWorkReportRules
} from '@/api/workReport'
import {
  isValidDateRange,
  isValidDeadlineTime,
  parsePositiveIntegerId
} from './composables/workReportGovernanceValidation.mjs'

const router = useRouter()
const tabs = [
  { value: 'rules', label: '报送规则' },
  { value: 'forcedRecipients', label: '强制接收人' },
  { value: 'exemptions', label: '人工豁免' },
  { value: 'quality', label: '组织数据质量' }
]
const activeTab = ref('rules')
const rules = ref([])
const rulesLoading = ref(false)
const rulesLoadError = ref(false)
const ruleFilter = reactive({ scope: '' })
const ruleDrawerVisible = ref(false)
const ruleSubmitting = ref(false)
const exemptionSubmitting = ref(false)
const forcedRecipientSubmitting = ref(false)
const ruleForm = reactive(defaultRuleForm())
const exemptionForm = reactive(defaultExemptionForm())
const forcedRecipientForm = reactive(defaultForcedRecipientForm())
const qualityForm = reactive({ deptId: '' })
const selectedRuleDept = ref(null)
const selectedRuleEmployee = ref(null)
const selectedExemptionEmployee = ref(null)
const selectedForcedScopeDept = ref(null)
const selectedForcedScopeEmployee = ref(null)
const selectedForcedRecipientUser = ref(null)
const filteredRules = computed(() => {
  if (!ruleFilter.scope) return rules.value
  return rules.value.filter((rule) => String(rule.scope || '').toUpperCase() === ruleFilter.scope)
})

onMounted(() => {
  loadRules()
})

watch(() => ruleForm.scope, () => {
  ruleForm.scopeTargetId = ''
  selectedRuleDept.value = null
  selectedRuleEmployee.value = null
})

watch(() => forcedRecipientForm.scope, () => {
  forcedRecipientForm.scopeTargetId = ''
  selectedForcedScopeDept.value = null
  selectedForcedScopeEmployee.value = null
})

async function loadRules() {
  rulesLoading.value = true
  rulesLoadError.value = false
  try {
    rules.value = normalizeRules(await getWorkReportRules())
  } catch {
    rules.value = []
    rulesLoadError.value = true
  } finally {
    rulesLoading.value = false
  }
}

function normalizeRules(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.records)) return data.records
  if (Array.isArray(data?.list)) return data.list
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.rules)) return data.rules
  return []
}

function resetRuleFilter() {
  ruleFilter.scope = ''
}

function openRuleDrawer() {
  Object.assign(ruleForm, defaultRuleForm())
  selectedRuleDept.value = null
  selectedRuleEmployee.value = null
  ruleDrawerVisible.value = true
}

function closeRuleDrawer() {
  if (!ruleSubmitting.value) ruleDrawerVisible.value = false
}

async function submitRule() {
  if (!ruleForm.versionNo || !ruleForm.effectiveFrom) {
    ElMessage.warning('请填写版本号和生效日期')
    return
  }
  if (!isValidDateRange(ruleForm.effectiveFrom, ruleForm.effectiveTo)) {
    ElMessage.warning('生效日期不能晚于失效日期')
    return
  }
  if (!isValidDeadlineTime(ruleForm.planDeadline) || !isValidDeadlineTime(ruleForm.summaryDeadline)) {
    ElMessage.warning('请填写有效的截止时间')
    return
  }
  if (ruleForm.teamPlanReminderEnabled && !Number.isInteger(Number(ruleForm.teamPlanReminderOffsetMinutes))) {
    ElMessage.warning('请填写有效的团队计划提醒时间')
    return
  }
  if (ruleForm.teamSummaryReminderEnabled && !Number.isInteger(Number(ruleForm.teamSummaryReminderOffsetMinutes))) {
    ElMessage.warning('请填写有效的团队总结提醒时间')
    return
  }

  const scopeTargetId = ruleForm.scope === 'TENANT'
    ? null
    : parsePositiveIntegerId(ruleForm.scopeTargetId)
  if (ruleForm.scope !== 'TENANT' && scopeTargetId === null) {
    ElMessage.warning(ruleForm.scope === 'DEPARTMENT' ? '请输入有效部门 ID' : '请输入有效员工 ID')
    return
  }

  ruleSubmitting.value = true
  try {
    await saveWorkReportRules({
      versionNo: Number(ruleForm.versionNo),
      scope: ruleForm.scope,
      scopeTargetId,
      effectiveFrom: ruleForm.effectiveFrom,
      effectiveTo: ruleForm.effectiveTo || null,
      planDeadline: ruleForm.planDeadline || null,
      summaryDeadline: ruleForm.summaryDeadline || null,
      dailyReportEnabled: ruleForm.dailyReportEnabled !== false,
      teamPlanReminderEnabled: ruleForm.teamPlanReminderEnabled === true,
      teamPlanReminderOffsetMinutes: ruleForm.teamPlanReminderEnabled
        ? Number(ruleForm.teamPlanReminderOffsetMinutes)
        : null,
      teamSummaryReminderEnabled: ruleForm.teamSummaryReminderEnabled === true,
      teamSummaryReminderOffsetMinutes: ruleForm.teamSummaryReminderEnabled
        ? Number(ruleForm.teamSummaryReminderOffsetMinutes)
        : null
    })
    ElMessage.success('报送规则已保存')
    ruleDrawerVisible.value = false
    await loadRules()
  } catch {
    ElMessage.error('报送规则保存失败，请稍后重试')
  } finally {
    ruleSubmitting.value = false
  }
}

async function submitExemption() {
  if (!exemptionForm.employeeUserId || !exemptionForm.exemptionDate || !exemptionForm.reason) {
    ElMessage.warning('请填写员工、日期和原因')
    return
  }

  const employeeUserId = parsePositiveIntegerId(exemptionForm.employeeUserId)
  if (employeeUserId === null) {
    ElMessage.warning('请输入有效员工用户 ID')
    return
  }

  exemptionSubmitting.value = true
  try {
    await saveWorkReportManualExemption({
      employeeUserId,
      exemptionDate: exemptionForm.exemptionDate,
      reason: exemptionForm.reason
    })
    ElMessage.success('人工豁免已创建')
    Object.assign(exemptionForm, defaultExemptionForm())
    selectedExemptionEmployee.value = null
  } catch {
    ElMessage.error('人工豁免创建失败，请稍后重试')
  } finally {
    exemptionSubmitting.value = false
  }
}

async function submitForcedRecipient() {
  const scopeTargetId = forcedRecipientForm.scope === 'TENANT'
    ? null
    : parsePositiveIntegerId(forcedRecipientForm.scopeTargetId)
  const userId = parsePositiveIntegerId(forcedRecipientForm.userId)

  if (forcedRecipientForm.scope !== 'TENANT' && scopeTargetId === null) {
    ElMessage.warning(forcedRecipientForm.scope === 'DEPARTMENT' ? '请选择有效部门' : '请选择有效员工')
    return
  }
  if (userId === null) {
    ElMessage.warning('请选择有效接收人')
    return
  }

  forcedRecipientSubmitting.value = true
  try {
    await saveForcedReportRecipients({
      scope: forcedRecipientForm.scope,
      scopeTargetId,
      userId,
      role: forcedRecipientForm.role,
      active: forcedRecipientForm.active
    })
    ElMessage.success('强制接收人已保存')
    Object.assign(forcedRecipientForm, defaultForcedRecipientForm())
    selectedForcedScopeDept.value = null
    selectedForcedScopeEmployee.value = null
    selectedForcedRecipientUser.value = null
  } catch {
    ElMessage.error('强制接收人保存失败，请稍后重试')
  } finally {
    forcedRecipientSubmitting.value = false
  }
}

function goDepartmentManagement() {
  router.push({
    path: '/system/dept',
    query: qualityForm.deptId ? { deptId: qualityForm.deptId } : {}
  })
}

function handleRuleDeptChange(value) {
  selectedRuleDept.value = value
  ruleForm.scopeTargetId = normalizeSelectorId(value)
}

function handleRuleEmployeeConfirm(users) {
  const [user] = Array.isArray(users) ? users : []
  selectedRuleEmployee.value = user || null
  ruleForm.scopeTargetId = normalizeSelectorId(user || selectedRuleEmployee.value)
}

function handleRuleEmployeeChange(value) {
  selectedRuleEmployee.value = value
  ruleForm.scopeTargetId = normalizeSelectorId(value)
}

function handleExemptionEmployeeConfirm(users) {
  const [user] = Array.isArray(users) ? users : []
  selectedExemptionEmployee.value = user || null
  exemptionForm.employeeUserId = normalizeSelectorId(user || selectedExemptionEmployee.value)
}

function handleExemptionEmployeeChange(value) {
  selectedExemptionEmployee.value = value
  exemptionForm.employeeUserId = normalizeSelectorId(value)
}

function handleForcedScopeDeptChange(value) {
  selectedForcedScopeDept.value = value
  forcedRecipientForm.scopeTargetId = normalizeSelectorId(value)
}

function handleForcedScopeEmployeeConfirm(users) {
  const [user] = Array.isArray(users) ? users : []
  selectedForcedScopeEmployee.value = user || null
  forcedRecipientForm.scopeTargetId = normalizeSelectorId(user || selectedForcedScopeEmployee.value)
}

function handleForcedScopeEmployeeChange(value) {
  selectedForcedScopeEmployee.value = value
  forcedRecipientForm.scopeTargetId = normalizeSelectorId(value)
}

function handleForcedRecipientUserConfirm(users) {
  const [user] = Array.isArray(users) ? users : []
  selectedForcedRecipientUser.value = user || null
  forcedRecipientForm.userId = normalizeSelectorId(user || selectedForcedRecipientUser.value)
}

function handleForcedRecipientUserChange(value) {
  selectedForcedRecipientUser.value = value
  forcedRecipientForm.userId = normalizeSelectorId(value)
}

function normalizeSelectorId(value) {
  if (Array.isArray(value)) return normalizeSelectorId(value[0])
  if (value && typeof value === 'object') return value.id ?? ''
  return value || ''
}

function defaultRuleForm() {
  return {
    scope: 'TENANT',
    scopeTargetId: '',
    versionNo: 1,
    effectiveFrom: localDate(),
    effectiveTo: '',
    planDeadline: '09:30',
    summaryDeadline: '18:30',
    dailyReportEnabled: true,
    teamPlanReminderEnabled: false,
    teamPlanReminderOffsetMinutes: -30,
    teamSummaryReminderEnabled: false,
    teamSummaryReminderOffsetMinutes: -20
  }
}

function defaultExemptionForm() {
  return {
    employeeUserId: '',
    exemptionDate: localDate(),
    reason: ''
  }
}

function defaultForcedRecipientForm() {
  return {
    scope: 'TENANT',
    scopeTargetId: '',
    userId: '',
    role: 'FORCED_RECIPIENT',
    active: true
  }
}

function scopeLabel(scope) {
  const normalized = String(scope || '').toUpperCase()
  if (normalized === 'TENANT') return '租户默认'
  if (normalized === 'DEPARTMENT') return '部门'
  if (normalized === 'EMPLOYEE') return '员工'
  return scope || '--'
}

function scopeTargetLabel(rule) {
  if (String(rule?.scope || '').toUpperCase() === 'TENANT') return '全租户'
  return rule?.scopeTargetName || rule?.targetName || rule?.scopeTargetId || '--'
}

function dateRangeLabel(rule) {
  const from = rule?.effectiveFrom || '--'
  const to = rule?.effectiveTo || '长期有效'
  return `${from} 至 ${to}`
}

function timeLabel(value) {
  if (!value) return '--'
  return String(value).slice(0, 5)
}

function reminderOffsetLabel(enabled, offset) {
  if (!enabled) return '未开启'
  const value = Number(offset)
  if (!Number.isFinite(value)) return '已开启'
  if (value === 0) return '截止时'
  return value < 0 ? `截止前 ${Math.abs(value)} 分钟` : `截止后 ${value} 分钟`
}

function localDate() {
  const now = new Date()
  const offset = now.getTimezoneOffset()
  return new Date(now.getTime() - offset * 60 * 1000).toISOString().slice(0, 10)
}
</script>
